/**
 * Reader profile. Everything we know about a reader: a name (given
 * freely), an email (given freely), and a signup timestamp (given
 * freely). It is stored in localStorage and it is the whole dossier.
 * Marketing has asked for more. Marketing has been given this file.
 */

import { readerState } from "./gate-bridge";

export interface ReaderProfile {
  name: string;
  email: string;
  at: string;
  universes: number;
}

const KEY = "wire.reader.v1";

export function read(): ReaderProfile | null {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<ReaderProfile>;
    if (typeof parsed.name !== "string" || typeof parsed.email !== "string") return null;
    return {
      name: parsed.name,
      email: parsed.email,
      at: parsed.at ?? "",
      universes: typeof parsed.universes === "number" ? parsed.universes : 1,
    };
  } catch {
    return null;
  }
}

export function displayName(): string {
  const profile = read();
  if (!profile) return "reader";
  return profile.name || "reader";
}

/** Tenure in the reader's own universe, to the day, optimistically. */
export function tenureDays(): number {
  const profile = read();
  if (!profile?.at) return 0;
  const then = new Date(profile.at).getTime();
  if (Number.isNaN(then)) return 0;
  return Math.max(0, Math.floor((Date.now() - then) / 86_400_000));
}

export function signedUp(): boolean {
  return readerState() === "signed-up";
}

/** For the colophon: "and you, <name>, reader of <n> universe(s)". */
export function colophonLine(): string {
  const profile = read();
  if (!profile) return "and you, reader, whenever you are ready";
  const n = profile.universes;
  return `and you, ${displayName()}, reader of ${n} universe${n === 1 ? "" : "s"}, day ${tenureDays()}`;
}

export function forget(): void {
  try {
    localStorage.removeItem(KEY);
  } catch {
    /* already forgotten */
  }
}
